import { I18N } from "oc-I18N";
import { isEmpty } from "oc-commons/Commons";
import bauseitsIcon from "../style/icons/bauseits-icon.svg";
import React, { MouseEventHandler, useState } from "react";
import { Tab, Tabs } from "./Tabs";
import { Jaco } from "oc-jaco3d/arrangement/Jaco";
import { ZElement } from "../arrangement/ZElement";
import { BaugruppenEintrag, BestelllistenEintrag } from "../arrangement/Stückliste";
import { UserDefinedArticleRows } from "./Stückliste-UserDefined";
import { MinimalNumberInput } from "../commons/MinimalNumberInput";

const isBauseits = ({ MassOrBauseitsTag }: { MassOrBauseitsTag: string }) =>
  MassOrBauseitsTag === "bauseits";

export function Stückliste({
  hideStückListe,
  onlyBestellliste = false,
  highlightElements = true,
}: {
  hideStückListe: MouseEventHandler<HTMLElement>;
  onlyBestellliste?: boolean;
  highlightElements?: boolean;
}) {
  const [activeTabKey, setActiveTabKey] = useState("Bestellliste");
  return (
    <div
      className="PartsList popup shadow"
      key="PartsList"
      role="dialog"
      aria-labelledby="stueckliste-title"
    >
      <button
        className="icon-close"
        onClick={hideStückListe}
        aria-label={I18N("aria::Stückliste schließen")}
      ></button>
      <Tabs
        data-tabs-key="Stückliste"
        id="stueckliste-title"
        className="tab-menu"
        activeTabKey={activeTabKey}
        setActiveTabKey={(key) => setActiveTabKey(key)}
      >
        <Tab tabKey="Bestellliste" header={I18N("Bestellliste")} className="">
          <Bestellliste highlightElements={highlightElements} />
        </Tab>
        {!onlyBestellliste && (
          <Tab tabKey="Baugruppen" header={I18N("Baugruppen")} className="">
            <Baugruppen highlightElements={highlightElements} />
          </Tab>
        )}
      </Tabs>
    </div>
  );
}

export function Bestellliste({ highlightElements = true }: { highlightElements?: boolean }) {
  const bestellliste = APP.arrangement.bestellliste;
  const userdefined = bestellliste?.filter((r) => r.UserDefined);
  const regular = bestellliste?.filter((r) => !r.UserDefined);
  return (
    <div className="partslist-table-big body">
      <table className="table-spacing">
        <thead>
          <tr>
            <th className="align-left">{I18N("Stk.")}</th>
            <th className="align-left">{I18N("Art.Nr.")}</th>
            <th className="align-left">{I18N("Beschreibung")}</th>
            <th className="align-right">{I18N("Einzelpreis")}</th>
            <th className="align-right">{I18N("Gesamtpreis")}</th>
          </tr>
        </thead>
        <tbody>
          {regular?.map(
            (
              {
                Anzahl,
                Artikelnummer,
                ArtikeltextPreisuebersicht,
                Mass,
                Massartikel,
                Einzelpreis,
                Preis,
                ArtikeltextPreisuebersichtExtra,
                StuecklistenanzahlEditierbar,
                EditierbarMin,
                EditierbarMax,
                AnzahlEditiert,
              },
              idx
            ) => {
              let editableClass = StuecklistenanzahlEditierbar ? "editable" : "";
              if (AnzahlEditiert) {
                editableClass += " edited";
              }

              return (
                <tr
                  key={idx}
                  className="partslist-group"
                  onMouseEnter={() => highlightElements && hoverByJacoIds(bestellliste[idx], true)}
                  onMouseLeave={() => highlightElements && hoverByJacoIds(bestellliste[idx], false)}
                  style={{ pointerEvents: "unset" }}
                >
                  <td className={`align-left vertical-align-top no-wrap ${editableClass}`}>
                    {StuecklistenanzahlEditierbar ? (
                      <MinimalNumberInput
                        id={"extra_article_" + idx}
                        ariaLabel={I18N("Artikel Anzahl")}
                        value={Anzahl}
                        onChange={(x) => {
                          const artikelID = bestellliste[idx].ArtikelID;
                          if (artikelID) {
                            APP.arrangement.customAnzahlen[artikelID] = x;
                            APP.arrangement.updateStückliste();
                            APP.logUserAction({
                              action: "editStücklistenAnzahl",
                              artikelID,
                              anzahl: x,
                            });
                            APP.ui.dirty();
                          }
                        }}
                        min={EditierbarMin || 0}
                        max={EditierbarMax}
                      />
                    ) : (
                      `${Anzahl} x `
                    )}
                  </td>
                  <td className="align-left vertical-align-top no-wrap">{Artikelnummer}</td>
                  <td className="align-left">
                    <span
                      dangerouslySetInnerHTML={{
                        __html: ArtikeltextPreisuebersicht,
                      }}
                    ></span>
                    {Massartikel && !isEmpty(Mass) && (
                      <>
                        <br /> <span>{Mass}</span>
                      </>
                    )}
                    {!isEmpty(ArtikeltextPreisuebersichtExtra) && (
                      <>
                        <br />
                        <span
                          dangerouslySetInnerHTML={{
                            __html: ArtikeltextPreisuebersichtExtra,
                          }}
                        ></span>
                      </>
                    )}
                  </td>
                  <td className="align-right vertical-align-top no-wrap">
                    {!isEmpty(Artikelnummer) && I18N.formatCurrency(Einzelpreis)}
                  </td>
                  <td className="align-right vertical-align-top no-wrap">
                    {!isEmpty(Artikelnummer) && I18N.formatCurrency(Preis)}
                  </td>
                </tr>
              );
            }
          )}
          <UserDefinedArticleRows allArticleRows={userdefined} />
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={5} className="hr"></th>
          </tr>
          <tr>
            <th colSpan={3} className="align-left" style={{ fontWeight: "unset" }}>
              {I18N("MwSt")}
            </th>
            <th colSpan={1} className="align-right">
              {I18N("Summe")}
            </th>
            <th className="align-right"> {I18N.formatCurrency(APP.arrangement.gesamtpreis)}</th>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export function Baugruppen({ highlightElements = true }: { highlightElements?: boolean }) {
  const baugruppen = APP.arrangement.baugruppen;

  return (
    <div className="partslist-table-big body">
      <table className="table-spacing">
        <thead>
          <tr>
            <th className="align-left">{I18N("Stk.")}</th>
            <th className="align-left">{I18N("Art.Nr.")}</th>
            <th className="align-left">{I18N("Icon")}</th>
            <th className="align-left">{I18N("Beschreibung")}</th>
          </tr>
        </thead>
        <tbody>
          {baugruppen.map((baugruppe, idx) =>
            baugruppe.map((Zeile, i) => (
              <tr
                key={idx + "_" + i}
                className="partslist-group"
                onMouseEnter={() => highlightElements && hoverByJacoIds(Zeile, true)}
                onMouseLeave={() => highlightElements && hoverByJacoIds(Zeile, false)}
              >
                <td className="align-left vertical-align-top no-wrap">{Zeile.Anzahl} x &nbsp;</td>
                <td className="align-left vertical-align-top no-wrap">{Zeile.Artikelnummer}</td>
                <td className="align-left vertical-align-top no-wrap">
                  {isBauseits(Zeile) && i == 0 ? (
                    <img src={bauseitsIcon} alt="bauseits icon" className="icon" />
                  ) : null}
                </td>
                <td
                  className={`align-left  ${
                    i === baugruppe.length - 1 ? "partslist-group-bottom" : ""
                  }`}
                >
                  <div
                    className={
                      i !== 0 ? (i !== baugruppe.length - 1 ? "group-mid" : "group-end") : ""
                    }
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: Zeile.ArtikeltextPreisuebersicht,
                      }}
                    ></span>{" "}
                    {!isEmpty(Zeile.MassOrBauseitsTag) && !isBauseits(Zeile) && i == 0 && (
                      <span>
                        <br /> {Zeile.MassOrBauseitsTag}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={6} className="hr"></th>
          </tr>
          <tr>
            <td colSpan={4} className="align-left no-wrap">
              {baugruppen.some((liste) => liste.some(isBauseits)) && (
                <>
                  <img src={bauseitsIcon} alt="bauseits icon" className="icon" />
                  {" : "}
                  {I18N("Muss bauseits gekürzt werden")}
                </>
              )}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

const isBaugruppenEintrag = (
  row: BestelllistenEintrag | BaugruppenEintrag
): row is BaugruppenEintrag => {
  return row.hasOwnProperty("MassOrBauseitsTag");
};
const hoverByJacoIds = (row: BestelllistenEintrag | BaugruppenEintrag, show: boolean) => {
  const toShow: Jaco[] = [];
  const ids = new Set(row.JacoIds);
  APP.arrangement.traverse((jaco) => {
    if (ids.has(jaco.id)) {
      toShow.push(jaco);
    }
  });

  toShow.forEach((el) => {
    (el as any).hovered = show;
  });
  APP.dirty();
};
